import { useState, useMemo, useCallback } from 'react';
import { ArrowRight, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Checkbox } from '../../components/ui/checkbox';
import { StepHeader } from '../../components/common/StepHeader';
import { EstimateCard } from '../../components/common/EstimateCard';
import { AdditionalDetails } from '../../components/wizard/AdditionalDetails';
import { CustomServiceRequest } from '../../components/wizard/CustomServiceRequest';
import { WizardNavigation } from '../../components/wizard/WizardNavigation';
import { MAX_ADDITIONAL_DETAILS } from '../../data/constants';
import { 
  HANDYMAN_SERVICE_CATEGORIES,
  HANDYMAN_TIMELINE_OPTIONS,
  PROJECT_SIZE_QUESTION,
  SERVICE_TO_FLOW_MAP
} from './handymanData';
import { calculateHandymanEstimate, getTriggeredFlows } from './handymanPricing';
import { hasServicesSelected, needsProjectSizeQuestion } from './handymanValidation';
import {
  InteriorRepairFlow,
  MountingFlow,
  DoorsLocksFlow,
  CarpentryFlow,
  SmallElectricalFlow,
  SmallPlumbingFlow
} from './flows';

// Flow phases
const PHASES = {
  SERVICE_SELECTION: 'service_selection',
  SUBFLOWS: 'subflows',
  PROJECT_SIZE: 'project_size',
  TIMELINE: 'timeline',
  ADDITIONAL_DETAILS: 'additional_details'
};

/**
 * Main Handyman Flow coordinator component
 * Manages all handyman sub-flows and orchestrates the wizard progression
 */
export const HandymanFlow = ({
  answers,
  setAnswers,
  onComplete,
  onBack,
  additionalDetails,
  setAdditionalDetails,
  unlistedServiceDescription = '',
  setUnlistedServiceDescription = () => {}
}) => {
  // Current phase of the wizard
  const [currentPhase, setCurrentPhase] = useState(PHASES.SERVICE_SELECTION);
  
  // For subflows: track which subflow is active and which have been completed
  const [activeSubflowIndex, setActiveSubflowIndex] = useState(0);
  const [completedSubflows, setCompletedSubflows] = useState([]);
  
  // Track expanded categories in service selection
  const [expandedCategories, setExpandedCategories] = useState([]);
  
  // Get selected services
  const selectedServices = useMemo(() => {
    return answers.handyman_services || [];
  }, [answers.handyman_services]);
  
  // Get triggered subflows from selected services
  const triggeredSubflows = useMemo(() => {
    return Array.from(getTriggeredFlows(selectedServices));
  }, [selectedServices]);
  
  // Calculate estimate
  const estimate = useMemo(() => {
    return calculateHandymanEstimate(answers);
  }, [answers]);
  
  // Toggle category expansion
  const toggleCategory = useCallback((categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  }, []);
  
  // Handle service selection toggle
  const handleServiceToggle = useCallback((serviceValue, checked) => {
    setAnswers(prev => {
      const currentServices = prev.handyman_services || [];
      const newServices = checked
        ? [...currentServices, serviceValue]
        : currentServices.filter(s => s !== serviceValue);
      return { ...prev, handyman_services: newServices };
    });
  }, [setAnswers]);
  
  // Get count of selected services per category
  const getSelectedCountForCategory = useCallback((categoryId) => {
    const category = HANDYMAN_SERVICE_CATEGORIES.find(c => c.id === categoryId);
    if (!category) return 0;
    return category.services.filter(s => selectedServices.includes(s.value)).length;
  }, [selectedServices]);
  
  // Handle proceeding from service selection
  const handleServiceSelectionNext = useCallback(() => {
    // If the customer only used the "Can't find what you need?" description
    // (no predefined services selected), skip subflows and go straight to timeline.
    if (!hasServicesSelected(selectedServices) && unlistedServiceDescription.trim()) {
      setCurrentPhase(PHASES.TIMELINE);
      return;
    }
    if (triggeredSubflows.length > 0) {
      // Go to subflows
      setCurrentPhase(PHASES.SUBFLOWS);
      setActiveSubflowIndex(0);
      setCompletedSubflows([]);
    } else if (needsProjectSizeQuestion(selectedServices)) {
      // Go to project size (no detailed subflows)
      setCurrentPhase(PHASES.PROJECT_SIZE);
    } else {
      // Go to timeline
      setCurrentPhase(PHASES.TIMELINE);
    }
  }, [triggeredSubflows, selectedServices, unlistedServiceDescription]);
  
  // Handle subflow completion
  const handleSubflowComplete = useCallback(() => {
    const currentSubflow = triggeredSubflows[activeSubflowIndex];
    setCompletedSubflows(prev => [...prev, currentSubflow]);
    
    if (activeSubflowIndex < triggeredSubflows.length - 1) {
      // Move to next subflow
      setActiveSubflowIndex(prev => prev + 1);
    } else {
      // All subflows complete, go to timeline
      setCurrentPhase(PHASES.TIMELINE);
    }
  }, [activeSubflowIndex, triggeredSubflows]);
  
  // Handle subflow back navigation
  const handleSubflowBack = useCallback(() => {
    if (activeSubflowIndex > 0) {
      setActiveSubflowIndex(prev => prev - 1);
      const previousSubflow = triggeredSubflows[activeSubflowIndex - 1];
      setCompletedSubflows(prev => prev.filter(s => s !== previousSubflow));
    } else {
      // Go back to service selection
      setCurrentPhase(PHASES.SERVICE_SELECTION);
    }
  }, [activeSubflowIndex, triggeredSubflows]);
  
  // Handle project size selection
  const handleProjectSizeSelect = useCallback((value) => {
    setAnswers(prev => ({ ...prev, project_size: value }));
  }, [setAnswers]);
  
  // Handle timeline selection
  const handleTimelineSelect = useCallback((value) => {
    setAnswers(prev => ({ ...prev, timeline: value }));
  }, [setAnswers]);
  
  // Handle back navigation based on current phase
  const handleBack = useCallback(() => {
    switch (currentPhase) {
      case PHASES.SERVICE_SELECTION:
        onBack();
        break;
      case PHASES.SUBFLOWS:
        handleSubflowBack();
        break;
      case PHASES.PROJECT_SIZE:
        setCurrentPhase(PHASES.SERVICE_SELECTION);
        break;
      case PHASES.TIMELINE:
        if (triggeredSubflows.length > 0) {
          setCurrentPhase(PHASES.SUBFLOWS);
          setActiveSubflowIndex(triggeredSubflows.length - 1);
        } else if (needsProjectSizeQuestion(selectedServices)) {
          setCurrentPhase(PHASES.PROJECT_SIZE);
        } else {
          setCurrentPhase(PHASES.SERVICE_SELECTION);
        }
        break;
      case PHASES.ADDITIONAL_DETAILS:
        setCurrentPhase(PHASES.TIMELINE);
        break;
      default:
        onBack();
    }
  }, [currentPhase, onBack, handleSubflowBack, triggeredSubflows, selectedServices]);
  
  // Render the current subflow component
  const renderCurrentSubflow = useCallback(() => {
    const currentSubflow = triggeredSubflows[activeSubflowIndex];
    
    const subflowProps = {
      answers,
      setAnswers,
      onComplete: handleSubflowComplete,
      onBack: handleSubflowBack
    };
    
    switch (currentSubflow) {
      case 'interior_repair':
        return <InteriorRepairFlow {...subflowProps} />;
      case 'hanging_mounting':
        return <MountingFlow {...subflowProps} />;
      case 'doors_locks':
        return <DoorsLocksFlow {...subflowProps} />;
      case 'carpentry':
        return <CarpentryFlow {...subflowProps} />;
      case 'small_electrical':
        return <SmallElectricalFlow {...subflowProps} />;
      case 'small_plumbing':
        return <SmallPlumbingFlow {...subflowProps} />;
      default:
        return null;
    }
  }, [triggeredSubflows, activeSubflowIndex, answers, setAnswers, handleSubflowComplete, handleSubflowBack]);
  
  // ==================== RENDER PHASES ====================
  
  // SERVICE SELECTION PHASE - Collapsible categories
  if (currentPhase === PHASES.SERVICE_SELECTION) {
    return (
      <div className="space-y-6">
        <StepHeader
          title="What do you need help with?"
          subtitle="Browse categories below and select all services that apply"
          onBack={onBack}
          showBack={true}
        />
        
        {/* Collapsible Categories */}
        <div className="space-y-3">
          {HANDYMAN_SERVICE_CATEGORIES.map((cat) => {
            const isExpanded = expandedCategories.includes(cat.id);
            const selectedCount = getSelectedCountForCategory(cat.id);
            
            return (
              <div key={cat.id} className="border border-gray-200 rounded-xl overflow-hidden">
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(cat.id)}
                  className={`w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors ${
                    isExpanded ? 'border-b border-gray-200' : ''
                  }`}
                  data-testid={`category-toggle-${cat.id}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{cat.emoji}</span>
                    <span className="font-medium text-[#1F2933]">{cat.name}</span>
                    {selectedCount > 0 && (
                      <span className="bg-[#0FA3B1] text-white text-xs px-2 py-0.5 rounded-full">
                        {selectedCount} selected
                      </span>
                    )}
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>
                
                {/* Category Services */}
                {isExpanded && (
                  <div className="p-3 space-y-2 bg-white" data-testid={`category-group-${cat.id}`}>
                    {cat.services.map((service) => {
                      const isSelected = selectedServices.includes(service.value);
                      return (
                        <label
                          key={service.value}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                            isSelected
                              ? 'bg-[#0FA3B1]/10 border border-[#0FA3B1]'
                              : 'bg-gray-50 border border-transparent hover:border-gray-200'
                          }`}
                          data-testid={`service-${service.value}`}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(checked) => handleServiceToggle(service.value, checked)}
                            className="border-2 border-gray-300 data-[state=checked]:bg-[#0FA3B1] data-[state=checked]:border-[#0FA3B1]"
                          />
                          <span className="text-[#1F2933] flex-1">{service.label}</span>
                          {isSelected && (
                            <Check className="w-4 h-4 text-[#0FA3B1]" />
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Running Estimate */}
        <EstimateCard priceRange={estimate} isCustomRequest={!estimate.hasValidPrice && !!unlistedServiceDescription.trim()} />
        
        {/* "Can't find what you need?" — customer can describe unlisted service */}
        <CustomServiceRequest
          value={unlistedServiceDescription}
          onChange={setUnlistedServiceDescription}
        />
        
        {/* Continue Button */}
        <WizardNavigation
          onBack={onBack}
          onContinue={handleServiceSelectionNext}
          continueDisabled={!hasServicesSelected(selectedServices) && !unlistedServiceDescription.trim()}
          continueTestId="handyman-services-next"
        />
      </div>
    );
  }
  
  // SUBFLOWS PHASE - Detailed questions for triggered subflows
  if (currentPhase === PHASES.SUBFLOWS) {
    return (
      <div className="space-y-6">
        {/* Subflow progress indicator */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
          <span>Details {activeSubflowIndex + 1} of {triggeredSubflows.length}</span>
          <div className="flex gap-1">
            {triggeredSubflows.map((flowName, idx) => (
              <div
                key={flowName}
                className={`w-2 h-2 rounded-full ${
                  idx < activeSubflowIndex ? 'bg-[#0FA3B1]' :
                  idx === activeSubflowIndex ? 'bg-[#0FA3B1]' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
        
        {renderCurrentSubflow()}
      </div>
    );
  }
  
  // PROJECT SIZE PHASE - For services without detailed subflows
  if (currentPhase === PHASES.PROJECT_SIZE) {
    return (
      <div className="space-y-6">
        <StepHeader
          title={PROJECT_SIZE_QUESTION.question}
          onBack={handleBack}
          showBack={true}
        />
        
        <RadioGroup
          value={answers.project_size || ""}
          onValueChange={handleProjectSizeSelect}
          className="space-y-3"
        >
          {PROJECT_SIZE_QUESTION.options.map((option) => (
            <label
              key={option.value}
              className={`flex items-center gap-4 p-4 bg-gray-50 border rounded-xl cursor-pointer transition-all hover:border-[#0FA3B1]/50 ${
                answers.project_size === option.value
                  ? "border-[#0FA3B1] bg-[#0FA3B1]/5"
                  : "border-gray-200"
              }`}
              data-testid={`option-${option.value}`}
            >
              <RadioGroupItem
                value={option.value}
                className="border-2 border-gray-300 text-[#0FA3B1]"
              />
              <span className="text-[#1F2933] flex-1">{option.label}</span>
            </label>
          ))}
        </RadioGroup>
        
        {/* Running Estimate */}
        <EstimateCard priceRange={estimate} isCustomRequest={!estimate.hasValidPrice && !!unlistedServiceDescription.trim()} />
        
        <WizardNavigation
          onBack={handleBack}
          onContinue={() => setCurrentPhase(PHASES.TIMELINE)}
          continueDisabled={!answers.project_size}
          continueTestId="project-size-next"
        />
      </div>
    );
  }
  
  // TIMELINE PHASE
  if (currentPhase === PHASES.TIMELINE) {
    return (
      <div className="space-y-6">
        <StepHeader
          title="When do you need this done?"
          onBack={handleBack}
          showBack={true}
        />
        
        <RadioGroup
          value={answers.timeline || ""}
          onValueChange={handleTimelineSelect}
          className="space-y-3"
        >
          {HANDYMAN_TIMELINE_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={`flex items-center gap-4 p-4 bg-gray-50 border rounded-xl cursor-pointer transition-all hover:border-[#0FA3B1]/50 ${
                answers.timeline === option.value
                  ? "border-[#0FA3B1] bg-[#0FA3B1]/5"
                  : "border-gray-200"
              }`}
              data-testid={`option-${option.value}`}
            >
              <RadioGroupItem
                value={option.value}
                className="border-2 border-gray-300 text-[#0FA3B1]"
              />
              <span className="text-[#1F2933] flex-1">{option.label}</span>
            </label>
          ))}
        </RadioGroup>
        
        {/* Running Estimate */}
        <EstimateCard priceRange={estimate} isCustomRequest={!estimate.hasValidPrice && !!unlistedServiceDescription.trim()} />
        
        <WizardNavigation
          onBack={handleBack}
          onContinue={() => setCurrentPhase(PHASES.ADDITIONAL_DETAILS)}
          continueDisabled={!answers.timeline}
          continueTestId="timeline-next"
        />
      </div>
    );
  }
  
  // ADDITIONAL DETAILS PHASE
  if (currentPhase === PHASES.ADDITIONAL_DETAILS) {
    return (
      <div className="space-y-6">
        <StepHeader
          title="Additional Details (Optional)"
          subtitle="Include any special requirements, access instructions, or specific requests"
          onBack={handleBack}
          showBack={true}
        />
        
        <AdditionalDetails
          value={additionalDetails || ''}
          onChange={setAdditionalDetails}
          label={null}
          placeholder="e.g., Need work done on the second floor, prefer morning appointments, specific brand preferences..."
          maxLength={MAX_ADDITIONAL_DETAILS}
          minHeight="150px"
        />
        
        {/* Running Estimate */}
        <EstimateCard priceRange={estimate} isCustomRequest={!estimate.hasValidPrice && !!unlistedServiceDescription.trim()} />
        
        <WizardNavigation
          onBack={handleBack}
          onContinue={onComplete}
          continueLabel="Get My Quote"
          continueTestId="handyman-complete"
        />
      </div>
    );
  }
  
  return null;
};

export default HandymanFlow;
